/* Header behaviour: mobile menu, mega menu, sticky state, localization auto-submit. */

/* Defined inside a guard so a second <script> tag for this file is a no-op rather than
   a redeclaration error. */
if (!customElements.get('wly-header')) {
  class WlyHeader extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-wly-menu-panel]');
      this.burger = this.querySelector('[data-wly-menu-open]');

      this.burger?.addEventListener('click', () => this.openMenu());
      this.querySelectorAll('[data-wly-menu-close]').forEach((el) => el.addEventListener('click', () => this.closeMenu()));

      this.setupMega();
      this.setupSticky();
      this.setupLocalization();

      // Close the mobile panel when the viewport grows past the breakpoint.
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.desktopQuery.addEventListener('change', (e) => {
        if (e.matches) this.closeMenu();
      });
    }

    /* ---------- Mobile menu ---------- */

    openMenu() {
      if (!this.panel) return;
      this.panel.hidden = false;
      this.burger?.setAttribute('aria-expanded', 'true');
      window.Wly.lockScroll();
      requestAnimationFrame(() => this.panel.classList.add('is-open'));
      window.Wly.trapFocus(this.panel, { onEscape: () => this.closeMenu() });
    }

    closeMenu() {
      if (!this.panel || this.panel.hidden) return;
      this.panel.classList.remove('is-open');
      this.burger?.setAttribute('aria-expanded', 'false');
      window.Wly.releaseFocus();
      window.Wly.unlockScroll();

      const finish = () => {
        this.panel.hidden = true;
      };
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) finish();
      else setTimeout(finish, 220);
    }

    /* ---------- Mega menu ---------- */

    setupMega() {
      this.megas = Array.from(this.querySelectorAll('[data-wly-mega]'));
      if (this.megas.length === 0) return;

      const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

      this.megas.forEach((details) => {
        const summary = details.querySelector('summary');

        // Hover opens on real pointers; click still works everywhere.
        details.addEventListener('mouseenter', () => {
          if (finePointer.matches) this.openMega(details);
        });
        details.addEventListener('mouseleave', () => {
          if (finePointer.matches) details.open = false;
        });

        // Only one panel open at a time.
        summary?.addEventListener('click', () => {
          this.megas.forEach((other) => {
            if (other !== details) other.open = false;
          });
        });

        details.addEventListener('focusout', (event) => {
          if (!details.contains(event.relatedTarget)) details.open = false;
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = this.megas.find((d) => d.open);
        if (!open) return;
        open.open = false;
        open.querySelector('summary')?.focus();
      });

      document.addEventListener('click', (event) => {
        if (this.contains(event.target)) return;
        this.megas.forEach((d) => {
          d.open = false;
        });
      });
    }

    openMega(details) {
      this.megas.forEach((other) => {
        if (other !== details) other.open = false;
      });
      details.open = true;
    }

    /* ---------- Sticky shadow ---------- */

    setupSticky() {
      if (this.dataset.sticky !== 'true') return;
      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;';
      this.parentNode.insertBefore(sentinel, this);

      new IntersectionObserver(
        ([entry]) => this.classList.toggle('is-stuck', !entry.isIntersecting),
        { threshold: 0 }
      ).observe(sentinel);
    }

    /* ---------- Localization ---------- */

    setupLocalization() {
      this.querySelectorAll('[data-wly-loc-select]').forEach((select) => {
        select.addEventListener('change', () => select.form?.submit());
      });
    }
  }

  customElements.define('wly-header', WlyHeader);
}
